import React, { useState, useRef, useEffect } from 'react';
import './InteractiveQuiz.css';

const InteractiveQuiz = ({
  questions = [], title = 'Quiz', level = 'B1', onClose,
  updateAttributes, node, editor, getPos,
  isStudent = false,
  myState = {}, otherState = {}, updateState = () => {},
}) => {
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editableQuestions, setEditableQuestions] = useState([...questions]);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableLevel, setEditableLevel] = useState(level);
  const addBtnRef = useRef(null);
  const dotsScrollRef = useRef(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const syncedRef = useRef(false);
  const prevResetAtRef = useRef(null);

  // Update gradient fades based on scroll/resize
  useEffect(() => {
    const el = dotsScrollRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setShowLeftFade(el.scrollLeft > 2);
      setShowRightFade(el.scrollLeft < max - 2);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  // Mount-restore
  useEffect(() => {
    if (syncedRef.current) return;
    const src = Object.keys(myState).length > 0 ? myState : (Object.keys(otherState).length > 0 ? otherState : null);
    if (!src) return;
    const hasAnswers = src.answers && Object.keys(src.answers).length > 0;
    if (!hasAnswers && !src.showResults) return;
    syncedRef.current = true;
    if (src.answers) setAnswers(src.answers);
    if (src.showResults) setShowResults(true);
    if (typeof src.currentQuestionIndex === 'number') setCurrentQuestionIndex(src.currentQuestionIndex);
  }, [myState, otherState]); // eslint-disable-line

  // Sync answer selections
  useEffect(() => {
    if (!otherState.answers) return;
    setAnswers(otherState.answers);
  }, [otherState.answers]); // eslint-disable-line

  // Sync question navigation
  useEffect(() => {
    if (typeof otherState.currentQuestionIndex !== 'number') return;
    setCurrentQuestionIndex(otherState.currentQuestionIndex);
  }, [otherState.currentQuestionIndex]); // eslint-disable-line

  // Sync results panel
  useEffect(() => {
    if (!otherState.showResults) return;
    setShowResults(true);
  }, [otherState.showResults]); // eslint-disable-line

  // Sync Try Again
  useEffect(() => {
    const resetAt = otherState.resetAt;
    if (!resetAt || resetAt === prevResetAtRef.current) return;
    prevResetAtRef.current = resetAt;
    setAnswers({});
    setShowResults(false);
    setCurrentQuestionIndex(0);
  }, [otherState.resetAt]); // eslint-disable-line

  const handleSelect = (questionId, optionId) => {
    if (showResults || isEditing) return;
    const next = { ...answers, [questionId]: optionId };
    setAnswers(next);
    updateState({ answers: next });
  };

  const handleSubmit = () => {
    setShowResults(true);
    updateState({ showResults: true });
  };

  const handleReset = () => {
    setAnswers({});
    setShowResults(false);
    setCurrentQuestionIndex(0);
    updateState({ answers: {}, showResults: false, currentQuestionIndex: 0, resetAt: Date.now() });
  };

  const handleNext = () => {
    if (currentQuestionIndex < editableQuestions.length - 1) {
      const next = currentQuestionIndex + 1;
      setCurrentQuestionIndex(next);
      updateState({ currentQuestionIndex: next });
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      const next = currentQuestionIndex - 1;
      setCurrentQuestionIndex(next);
      updateState({ currentQuestionIndex: next });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setShowResults(false);
  };

  const handleSave = () => {
    setIsEditing(false);
    if (typeof updateAttributes === 'function') {
      updateAttributes({
        questions: editableQuestions,
        title: editableTitle,
        level: editableLevel,
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableQuestions([...questions]);
    setEditableTitle(title);
  };

  const updateQuestion = (questionId, field, value) => {
    setEditableQuestions(prev =>
      prev.map(q =>
        q.id === questionId ? { ...q, [field]: value } : q
      )
    );
  };

  const updateOption = (questionId, optionId, field, value) => {
    setEditableQuestions(prev =>
      prev.map(q =>
        q.id === questionId ? {
          ...q,
          options: q.options.map(opt =>
            opt.id === optionId ? { ...opt, [field]: value } : opt
          )
        } : q
      )
    );
  };

  const getScore = () => {
    let score = 0;
    editableQuestions.forEach(q => {
      const correctOption = q.options.find(opt => opt.isCorrect);
      if (answers[q.id] === correctOption?.id) {
        score++;
      }
    });
    return score;
  };

  const getOptionClass = (questionId, optionId) => {
    if (!showResults || isEditing) {
      return answers[questionId] === optionId ? 'selected' : '';
    }

    const question = editableQuestions.find(q => q.id === questionId);
    const option = question?.options.find(opt => opt.id === optionId);

    if (option?.isCorrect) return 'correct';
    if (answers[questionId] === optionId && !option?.isCorrect) return 'incorrect';
    return '';
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: `q${Date.now()}`,
      question: '',
      options: [
        { id: 'a', text: '', isCorrect: false },
        { id: 'b', text: '', isCorrect: false },
        { id: 'c', text: '', isCorrect: false },
        { id: 'd', text: '', isCorrect: false }
      ]
    };
    setEditableQuestions(prev => {
      const updated = [...prev, newQuestion];
      setCurrentQuestionIndex(updated.length - 1);
      return updated;
    });
  };

  const handleDeleteQuestion = (questionIndex) => {
    if (editableQuestions.length <= 1) return; // Не удаляем последний вопрос

    setEditableQuestions(prev => {
      const updated = prev.filter((_, index) => index !== questionIndex);
      // Корректируем индекс текущего вопроса
      if (currentQuestionIndex >= questionIndex) {
        setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1));
      }
      return updated;
    });
  };

  return (
    <div className="qz-card">
      <div className="qz-header">
        <div className="qz-header-left">
          <div className="qz-header-icon">
            <svg width="20" height="20" viewBox="0 0 512 512" fill="white">
              <g>
                <path d="m473.909 188.982h-121.034c14.132-19.61 22.47-43.66 22.47-69.624 0-65.814-53.544-119.358-119.36-119.358-65.814 0-119.358 53.544-119.358 119.358 0 25.964 8.338 50.014 22.47 69.624h-121.006c-21.003 0-38.091 17.088-38.091 38.091v192.355c0 21.003 17.088 38.091 38.091 38.091h164.723l45.716 51.145c1.897 2.122 4.608 3.335 7.454 3.336h.002c2.846 0 5.557-1.212 7.454-3.333l45.746-51.147h164.724c21.003 0 38.091-17.087 38.091-38.091v-192.356c-.001-21.003-17.089-38.091-38.092-38.091zm-217.924-168.982c54.787 0 99.359 44.572 99.359 99.358s-44.572 99.359-99.359 99.359c-54.786 0-99.358-44.572-99.358-99.359s44.572-99.358 99.358-99.358zm236.015 399.428c0 9.976-8.115 18.091-18.091 18.091h-169.196c-2.846 0-5.557 1.212-7.454 3.333l-41.271 46.145-41.244-46.142c-1.897-2.123-4.609-3.336-7.456-3.336h-169.197c-9.976 0-18.091-8.115-18.091-18.091v-192.355c0-9.975 8.115-18.091 18.091-18.091h139.157c21.03 18.498 48.594 29.735 78.737 29.735s57.708-11.237 78.738-29.735h139.186c9.976 0 18.091 8.116 18.091 18.091z"/>
                <path d="m326.907 266.083c-5.522 0-10 4.477-10 10v96.404c0 5.523 4.478 10 10 10s10-4.477 10-10v-96.404c0-5.523-4.477-10-10-10z"/>
                <path d="m446.641 362.487h-45.866l52.742-80.945c2.003-3.074 2.161-6.998.413-10.224s-5.123-5.235-8.792-5.235h-60.83c-5.522 0-10 4.477-10 10s4.478 10 10 10h42.379l-52.741 80.945c-2.004 3.074-2.162 6.998-.414 10.224s5.123 5.235 8.792 5.235h64.317c5.522 0 10-4.477 10-10s-4.478-10-10-10z"/>
                <path d="m271.265 266.083c-5.522 0-10 4.477-10 10v66.273c0 4.791-1.313 11.822-12.688 17.554-3.376 1.71-7.216 2.577-11.373 2.577-4.683-.018-8.745-1.171-12.439-3.537-7.444-4.741-10.48-9.532-10.48-16.538v-66.33c0-5.523-4.478-10-10-10s-10 4.477-10 10v66.33c0 14.021 6.456 24.949 19.716 33.394 6.871 4.4 14.652 6.647 23.165 6.681 7.365 0 14.245-1.593 20.431-4.726 19.563-9.857 23.67-24.694 23.67-35.405v-66.273c-.002-5.523-4.48-10-10.002-10z"/>
                <path d="m161.439 357.283c6.485-9.389 10.296-20.761 10.296-33.012 0-32.085-26.103-58.188-58.188-58.188-32.101 0-58.217 26.103-58.217 58.188 0 32.101 26.116 58.216 58.217 58.216 12.633 0 24.33-4.062 33.88-10.931l7.606 7.606c1.953 1.953 4.512 2.929 7.071 2.929s5.118-.977 7.071-2.929c3.905-3.905 3.905-10.237 0-14.142zm-47.891 5.204c-21.073 0-38.217-17.144-38.217-38.216 0-21.057 17.144-38.188 38.217-38.188 21.057 0 38.188 17.131 38.188 38.188 0 6.714-1.745 13.025-4.795 18.513l-8.605-8.605c-3.906-3.905-10.236-3.905-14.143 0-3.905 3.905-3.905 10.237 0 14.142l8.814 8.814c-5.703 3.395-12.356 5.352-19.459 5.352z"/>
                <path d="m255.985 175.773c-5.522 0-10 4.477-10 10v.057c0 5.523 4.478 9.972 10 9.972s10-4.505 10-10.028-4.477-10.001-10-10.001z"/>
                <path d="m225.655 93.217c5.522 0 10-4.477 10-10 0-1.043.061-1.972.188-2.861 1.429-10.182 10.271-17.675 20.551-17.5 10.613.229 19.357 8.691 19.907 19.265.293 5.641-1.682 10.997-5.56 15.082-3.885 4.092-9.125 6.345-14.756 6.345-5.522 0-10 4.477-10 10v31.719c0 5.523 4.478 10 10 10s10-4.477 10-10v-22.99c7.29-1.877 14.012-5.774 19.261-11.303 7.691-8.102 11.608-18.717 11.027-29.891-1.091-20.979-18.419-37.768-39.464-38.222-.273-.005-.545-.008-.816-.008-20.058 0-37.158 14.794-39.953 34.7-.259 1.812-.385 3.666-.385 5.665 0 5.522 4.478 9.999 10 9.999z"/>
              </g>
            </svg>
          </div>
          {isEditing ? (
            <input
              type="text"
              value={editableTitle}
              onChange={(e) => setEditableTitle(e.target.value)}
              className="qz-title-input"
            />
          ) : (
            <span className="qz-title">{editableTitle}</span>
          )}
          {isEditing ? (
            <input
              type="text"
              value={editableLevel}
              onChange={(e) => setEditableLevel(e.target.value)}
              className="qz-level-input"
              placeholder="Level"
            />
          ) : (
            <span className="qz-level-badge">{editableLevel}</span>
          )}
        </div>
        <div className="qz-header-actions">
          {!isEditing && !showResults && !isStudent && (
            <button className="qz-icon-btn" aria-label="Edit" data-tip="Edit" onClick={handleEdit}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {!isEditing && onClose && (
            <button className="qz-icon-btn" aria-label="Close" data-tip="Close" onClick={onClose}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <path d="M2 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-closed" style={{ opacity: 0 }} />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="qz-body">
        <div className="qz-questions-container">
          {editableQuestions.length > 0 && editableQuestions[currentQuestionIndex] && (
            <div className="qz-question-card">
              <div className="qz-question-chip">
                Question {currentQuestionIndex + 1} of {editableQuestions.length}
              </div>
              <div className="qz-question-text">
                {isEditing ? (
                  <input
                    type="text"
                    value={editableQuestions[currentQuestionIndex]?.question || ''}
                    onChange={(e) => updateQuestion(editableQuestions[currentQuestionIndex]?.id, 'question', e.target.value)}
                    className="qz-question-input"
                    placeholder="Enter question..."
                  />
                ) : (
                  editableQuestions[currentQuestionIndex]?.question || ''
                )}
              </div>
              <div className="qz-options-list">
                {editableQuestions[currentQuestionIndex]?.options?.map(option => {
                  const className = `qz-option-btn ${getOptionClass(editableQuestions[currentQuestionIndex]?.id, option.id)}`;
                  return isEditing ? (
                    <div key={option.id} className={className}>
                      <div className="qz-option-editor">
                        <input
                          type="radio"
                          name={`correct-${editableQuestions[currentQuestionIndex]?.id}`}
                          checked={option.isCorrect}
                          onChange={() => {
                            // Set all options to false first
                            editableQuestions[currentQuestionIndex]?.options?.forEach(opt => {
                              updateOption(editableQuestions[currentQuestionIndex]?.id, opt.id, 'isCorrect', false);
                            });
                            // Set this option to true
                            updateOption(editableQuestions[currentQuestionIndex]?.id, option.id, 'isCorrect', true);
                          }}
                          className="qz-correct-radio"
                        />
                        <input
                          type="text"
                          value={option.text}
                          onChange={(e) => updateOption(editableQuestions[currentQuestionIndex]?.id, option.id, 'text', e.target.value)}
                          className="qz-option-input"
                          placeholder={`Option ${option.id.toUpperCase()}`}
                        />
                      </div>
                    </div>
                  ) : (
                    <button
                      key={option.id}
                      className={className}
                      onClick={() => handleSelect(editableQuestions[currentQuestionIndex]?.id, option.id)}
                      disabled={showResults}
                    >
                      <span className="qz-option-label" aria-hidden>{option.id.toUpperCase()}</span>
                      <span className="qz-option-text">{option.text}</span>
                      {showResults && option.isCorrect && (
                        <span className="qz-correct-indicator">✓</span>
                      )}
                    </button>
                  );
                })}
              </div>
              {!isStudent && !isEditing && (() => {
                const correct = editableQuestions[currentQuestionIndex]?.options?.find(o => o.isCorrect);
                if (!correct) return null;
                return (
                  <div className="qz-teacher-hint" role="note" aria-label="Only visible to teacher">
                    <span className="qz-teacher-hint-badge" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.79 19.79 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.79 19.79 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                      Only visible to you
                    </span>
                    <span className="qz-teacher-hint-answer">
                      Correct: <strong>{correct.id.toUpperCase()}. {correct.text}</strong>
                    </span>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Навигация по вопросам и точки */}
        {editableQuestions.length > 1 && (
          <div className="qz-navigation">
            {currentQuestionIndex > 0 ? (
              <button className="qz-nav-btn qz-prev-btn" onClick={handlePrevious}>
                Previous
              </button>
            ) : <div className="qz-nav-btn-placeholder"></div>}
            <div className="qz-question-dots">
              {(() => {
                const perDot = 40; // px
                const gap = 8; // px
                const visibleSlots = Math.min(editableQuestions.length, 5);
                const scrollWidth = visibleSlots > 0 ? Math.round((visibleSlots * perDot) + ((visibleSlots - 1) * gap)) : 0;
                return (
              <>
                <div
                  ref={dotsScrollRef}
                  className="qz-question-dots-scroll"
                  style={{
                    display: 'flex',
                    gap: 8,
                    overflowX: 'auto',
                    maxWidth: scrollWidth,
                    WebkitMaskImage: (() => {
                      const pad = 28;
                      if (showLeftFade && showRightFade) {
                        return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                      }
                      if (showLeftFade && !showRightFade) {
                        return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 100%)`;
                      }
                      if (!showLeftFade && showRightFade) {
                        return `linear-gradient(to right, #000 0%, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                      }
                      return 'none';
                    })(),
                    maskImage: (() => {
                      const pad = 28;
                      if (showLeftFade && showRightFade) {
                        return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                      }
                      if (showLeftFade && !showRightFade) {
                        return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 100%)`;
                      }
                      if (!showLeftFade && showRightFade) {
                        return `linear-gradient(to right, #000 0%, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                      }
                      return 'none';
                    })()
                  }}
                >
                  {editableQuestions.map((q, index) => {
                  let dotClass = "qz-dot";
                  const qid = q.id;
                  if (showResults && !isEditing) {
                    const selected = answers[qid];
                    const correctOption = q.options.find(opt => opt.isCorrect)?.id;
                    if (selected) {
                      if (selected === correctOption) {
                        dotClass += " dot-correct";
                      } else {
                        dotClass += " dot-incorrect";
                      }
                    }
                  } else if (answers[qid]) {
                    dotClass += " answered";
                  }
                  if (index === currentQuestionIndex) dotClass += " active";
                  return (
                    <div key={index} style={{ position: 'relative', flex: '0 0 auto' }}>
                      <button
                        className={dotClass}
                        onClick={() => { setCurrentQuestionIndex(index); updateState({ currentQuestionIndex: index }); }}
                      >
                        {index + 1}
                      </button>
                      {isEditing && editableQuestions.length > 1 && (
                        <button
                          className="qz-dot-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteQuestion(index);
                          }}
                          title="Delete question"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  );
                  })}
                </div>
                {/* No overlay fades; smooth edges come from mask on scroller (like ABCD) */}
              </>
                );
              })()}
              {isEditing && (
                <div style={{ position: 'relative', marginLeft: 12 }}>
                  <button
                    ref={addBtnRef}
                    className="qz-dot add-dot"
                    onClick={handleAddQuestion}
                    title="Add question"
                    type="button"
                  >
                    +
                  </button>
                </div>
              )}
            </div>
            {currentQuestionIndex < editableQuestions.length - 1 ? (
              <button className="qz-nav-btn qz-next-btn" onClick={handleNext}>
                Next
              </button>
            ) : <div className="qz-nav-btn-placeholder"></div>}
          </div>
        )}

        <div className="qz-actions">
          {!showResults && !isEditing ? (
            <button
              className="qz-submit-btn"
              onClick={handleSubmit}
              disabled={Object.keys(answers).length !== editableQuestions.length}
            >
              Submit Answers
            </button>
          ) : showResults && !isEditing ? (() => {
            const displayScore = getScore();
            const total = editableQuestions.length;
            const percent = total ? Math.round((displayScore / total) * 100) : 0;
            const incorrect = total - displayScore;
            const circumference = 2 * Math.PI * 42;
            const dashOffset = circumference * (1 - percent / 100);
            let tier = 'meh';
            let headline;
            let subline;
            if (percent >= 90) { tier = 'excellent'; headline = 'Outstanding!'; subline = isStudent ? 'You nailed it.' : 'Student nailed it.'; }
            else if (percent >= 70) { tier = 'great'; headline = 'Great job!'; subline = 'Solid performance.'; }
            else if (percent >= 50) { tier = 'good'; headline = 'Good work.'; subline = isStudent ? 'Keep going — you’re close.' : 'Getting there.'; }
            else { tier = 'practice'; headline = 'Keep practicing'; subline = isStudent ? 'You’ll get it — try again.' : 'Review and try again.'; }
            return (
              <div className={`qz-results tier-${tier}`}>
                <div className="qz-results-ring-wrap">
                  <svg viewBox="0 0 100 100" className="qz-results-ring" aria-hidden>
                    <circle cx="50" cy="50" r="42" className="qz-results-ring-track" />
                    <circle
                      cx="50"
                      cy="50"
                      r="42"
                      className="qz-results-ring-progress"
                      style={{ strokeDasharray: circumference, strokeDashoffset: dashOffset }}
                    />
                  </svg>
                  <div className="qz-results-ring-center">
                    <div className="qz-results-percent">{percent}%</div>
                    <div className="qz-results-percent-label">score</div>
                  </div>
                </div>
                <div className="qz-results-headline">{headline}</div>
                <div className="qz-results-subline">{subline}</div>
                <div className="qz-results-stats">
                  <div className="qz-results-stat is-correct">
                    <span className="qz-results-stat-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                    <span className="qz-results-stat-value">{displayScore}</span>
                    <span className="qz-results-stat-label">correct</span>
                  </div>
                  <div className="qz-results-stats-divider" aria-hidden />
                  <div className="qz-results-stat is-incorrect">
                    <span className="qz-results-stat-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </span>
                    <span className="qz-results-stat-value">{incorrect}</span>
                    <span className="qz-results-stat-label">incorrect</span>
                  </div>
                </div>
                <button className="qz-results-cta" onClick={handleReset}>
                  <span>Try Again</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="23 4 23 10 17 10"/>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                  </svg>
                </button>
              </div>
            );
          })() : null}
        </div>
      </div>

      {isEditing && (
        <div className="qz-footer">
          <button className="qz-cancel-btn" onClick={handleCancel}>Cancel</button>
          <button className="qz-save-btn" onClick={handleSave}>Save</button>
        </div>
      )}
    </div>
  );
};

export default InteractiveQuiz;
